/**
 * Switch Field Component
 * Wrapper for MUI Switch with react-hook-form integration
 */
import React from 'react'
import {
  FormControlLabel,
  Switch,
  FormHelperText,
  FormControl,
  Typography
} from '@mui/material'
import { Controller } from 'react-hook-form'
import { FormFieldProps } from '../types'
import { useFormContext } from '../FormContext'

const SwitchField: React.FC<FormFieldProps> = ({
  config,
  control,
  errors,
  readOnly,
  values
}) => {
  const { name, label, disabled, helperText, fieldProps = {} } = config

  // Get original values from form context for change detection
  const formContext = useFormContext()
  const formValues = formContext?.values || {}
  const currentValue = values?.[name] || false

  // Check if the current field has been modified
  const isFieldChanged = () => {
    // Check field-level configuration, return false if change highlighting is not enabled
    if (config.enableChangeHighlight !== true) {
      return false
    }

    // If change highlighting is enabled, check if the field has changed
    if (formContext) {
      const originalValues = formContext.originalValues || {}
      const currentValues = formValues

      // Direct comparison of original and current values
      const originalValue = originalValues[name]
      const currentValue = currentValues[name]

      // Special handling for boolean values to ensure comparison is done on boolean values
      const origBoolValue =
        originalValue === true ||
        originalValue === 'true' ||
        originalValue === 1
      const currBoolValue =
        currentValue === true || currentValue === 'true' || currentValue === 1

      // Only return true when values are actually different
      return origBoolValue !== currBoolValue
    }
    return false
  }

  // Determine whether to apply highlight style
  const isChanged = isFieldChanged()

  // Handle readOnly type issue
  const isReadOnly =
    typeof readOnly === 'boolean'
      ? readOnly
      : Array.isArray(readOnly)
        ? readOnly.includes(name)
        : false

  // Get label text
  const getLabelText = () => {
    // Check if there is a custom label function
    if (fieldProps.label && typeof fieldProps.label === 'function') {
      return fieldProps.label(currentValue)
    }
    return label
  }

  // Create label with highlight style
  const labelSx = fieldProps.labelSx || {}
  const styledLabel = isChanged ? (
    <Typography
      component='span'
      sx={{
        color: 'secondary.dark',
        fontWeight: 'bold',
        ...labelSx
      }}>
      {getLabelText()}
    </Typography>
  ) : labelSx && Object.keys(labelSx).length > 0 ? (
    <Typography component='span' sx={labelSx}>
      {getLabelText()}
    </Typography>
  ) : (
    getLabelText()
  )

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value, ...field } }) => (
        <FormControl error={!!errors[name]}>
          <FormControlLabel
            control={
              <Switch
                size='small'
                checked={!!value}
                onChange={e => onChange(e.target.checked)}
                disabled={disabled || isReadOnly}
                {...field}
                {...(fieldProps.label
                  ? {}
                  : (() => {
                      const { labelSx: _, ...rest } = fieldProps
                      return rest
                    })())}
              />
            }
            label={styledLabel}
          />
          {(!!errors[name] || helperText) && (
            <FormHelperText>
              {errors[name]?.message || helperText}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  )
}

export default SwitchField
